/**
 * ChatInput — Eingabefeld am unteren Rand des Panels
 *
 * Verhalten:
 *   - Auto-Grow Textarea (1 Zeile bis ~5 Zeilen, dann Scroll)
 *   - Enter sendet, Shift+Enter macht Zeilenumbruch
 *   - Send-Button: rund, gold, nur aktiv wenn Inhalt vorhanden (Text ODER Bilder)
 *   - Anhang-Button: Bilder anhängen (max 3 pro Nachricht), öffnet File-Picker
 *   - Drag-and-Drop: Bilder direkt ins Composer-Feld ziehen
 *   - Paste (Cmd/Strg+V): Bild aus Zwischenablage einfügen
 *   - Vorschau-Strip: zeigt angehängte Bilder mit X-Button zum Entfernen
 *   - Bei Read-Only-Konversation (Abo gekündigt): Hinweis statt Eingabefeld
 *   - Wird beim Mount des Panels NICHT automatisch fokussiert (Mobile würde
 *     sonst die Tastatur aufpoppen lassen, irritierend)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Send, Paperclip, X, CornerUpLeft, Image as ImageIcon } from 'lucide-react';
import type { PendingReply } from './ChatPanel';

const MAX_IMAGES = 3;
const ACCEPTED_MIME = /^image\/(jpeg|jpg|png|webp|gif)$/;

interface ChatInputProps {
  /**
   * Sendet den eingegebenen Text + optionale Bilder + optionale Captions
   * (gleicher Index) + optionale Reply-Referenz.
   */
  onSend: (
    content: string,
    images?: File[],
    captions?: (string | null)[],
    replyToMessageId?: string | null
  ) => Promise<void>;
  /** Wenn true, wird das Eingabefeld disabled mit Hinweis (Read-Only-Phase). */
  readOnly?: boolean;
  /** Read-Only-Hinweistext (z.B. mit Datum bis wann der Verlauf einsehbar ist). */
  readOnlyHint?: string;
  /** Aktuelle Antwort-Referenz, falls der User auf eine Bubble geswipet hat. */
  pendingReply?: PendingReply | null;
  /** Callback: User klickt das X auf der Reply-Vorschau oder sendet die Nachricht. */
  onClearReply?: () => void;
}

interface AttachedImage {
  file: File;
  previewUrl: string;  // ObjectURL — wird beim removeImage / handleSend revoked
  caption: string;     // optional, max 120 Zeichen
}

const CAPTION_MAX = 120;

export function ChatInput({
  onSend,
  readOnly,
  readOnlyHint,
  pendingReply,
  onClearReply
}: ChatInputProps): React.ReactElement {
  const [value, setValue] = useState('');
  const [attached, setAttached] = useState<AttachedImage[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-Grow: Höhe an Inhalt anpassen, max. 5 Zeilen
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const maxHeight = 5 * 20;
    ta.style.height = `${Math.min(ta.scrollHeight, maxHeight)}px`;
  }, [value]);

  // Beim Unmount: alle ObjektURLs freigeben.
  // Bewusst KEINE images-Dependency — sonst würde React bei jedem Add/Remove
  // die gerade neu erstellten URLs sofort wieder revoken (Race-Condition).
  useEffect(() => {
    return () => {
      // Snapshot zum Cleanup-Zeitpunkt
      setAttached((current) => {
        for (const a of current) {
          try { URL.revokeObjectURL(a.previewUrl); } catch { /* ignore */ }
        }
        return current;
      });
    };
  }, []);

  const trimmed = value.trim();
  const hasImages = attached.length > 0;
  const canSend = (trimmed.length > 0 || hasImages) && !isSending && !readOnly;

  // Bilder hinzufügen mit Validierung (Type + Max-Anzahl)
  const addImages = (newFiles: FileList | File[]): void => {
    setError(null);
    const incoming = Array.from(newFiles);
    if (incoming.length === 0) return;

    const valid: AttachedImage[] = [];
    const rejected: string[] = [];
    for (const file of incoming) {
      if (!ACCEPTED_MIME.test(file.type)) {
        rejected.push(file.name + ' (kein Bild)');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        rejected.push(file.name + ' (über 10 MB)');
        continue;
      }
      valid.push({ file, previewUrl: URL.createObjectURL(file), caption: '' });
    }

    setAttached((prev) => {
      const combined = [...prev, ...valid];
      if (combined.length > MAX_IMAGES) {
        // Überschüssige ObjektURLs revoken, sonst Memory-Leak
        for (const dropped of combined.slice(MAX_IMAGES)) {
          try { URL.revokeObjectURL(dropped.previewUrl); } catch { /* ignore */ }
        }
        setError(`Maximal ${MAX_IMAGES} Bilder pro Nachricht. Überschüssige verworfen.`);
        return combined.slice(0, MAX_IMAGES);
      }
      if (rejected.length > 0) {
        setError('Verworfen: ' + rejected.join(', '));
      }
      return combined;
    });
  };

  const removeImage = (idx: number): void => {
    setAttached((prev) => {
      const removed = prev[idx];
      if (removed) {
        try { URL.revokeObjectURL(removed.previewUrl); } catch { /* ignore */ }
      }
      return prev.filter((_, i) => i !== idx);
    });
    setError(null);
  };

  const setCaption = (idx: number, caption: string): void => {
    setAttached((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, caption: caption.slice(0, CAPTION_MAX) } : a))
    );
  };

  const handleSend = async (): Promise<void> => {
    if (!canSend) return;
    const content = trimmed;
    const filesToSend = attached.map((a) => a.file);
    const captionsToSend = attached.map((a) => a.caption.trim() || null);
    const urlsToRevoke = attached.map((a) => a.previewUrl);
    const replyId = pendingReply?.messageId ?? null;
    setValue('');
    setAttached([]);
    setError(null);
    setIsSending(true);
    // Reply-Vorschau direkt schließen — auch wenn der Send fehlschlägt, soll
    // der User den Reply nicht erneut anhaften haben (sonst doppelt-zitieren).
    onClearReply?.();
    try {
      await onSend(
        content,
        filesToSend.length > 0 ? filesToSend : undefined,
        filesToSend.length > 0 ? captionsToSend : undefined,
        replyId
      );
    } catch (err) {
      console.error('[ChatInput] onSend failed:', err);
    } finally {
      for (const url of urlsToRevoke) {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }
      setIsSending(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // Paste-Handler: Bilder aus Zwischenablage erkennen
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addImages(files);
    }
  };

  // Drag-and-Drop Handler
  const handleDragEnter = (e: React.DragEvent): void => {
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      setIsDragOver(true);
    }
  };
  const handleDragOver = (e: React.DragEvent): void => {
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      setIsDragOver(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent): void => {
    if (e.currentTarget === e.target) {
      setIsDragOver(false);
    }
  };
  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files;
    if (dropped && dropped.length > 0) {
      addImages(dropped);
    }
  };

  // Read-Only-Modus
  if (readOnly) {
    return (
      <div className="border-t border-gray-300/70 dark:border-white/5 bg-white/40 dark:bg-transparent px-4 py-3">
        <p className="text-[12px] opacity-60 text-center leading-relaxed">
          {readOnlyHint ?? 'Diese Konversation ist nur lesbar.'}
        </p>
      </div>
    );
  }

  const canAttachMore = attached.length < MAX_IMAGES;

  return (
    <div
      className={`
        border-t border-gray-300/70 dark:border-white/5
        bg-white/40 dark:bg-transparent
        shadow-[inset_0_1px_0_0_rgba(255,255,255,0.6)] dark:shadow-none
        relative
        transition-colors duration-150
        ${isDragOver ? '!bg-brand/10' : ''}
      `}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag-Overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center bg-brand/15 border-2 border-dashed border-brand/60 rounded-2xl m-2">
          <p className="text-[13px] font-semibold text-brand">Bilder hier loslassen</p>
        </div>
      )}

      {/* Reply-Vorschau: zeigt die zitierte Nachricht plus X-Button zum Verwerfen */}
      {pendingReply && (
        <div className="
          mx-3 mt-3 px-3 py-2
          flex items-start gap-2
          rounded-xl
          bg-brand/10 dark:bg-brand/15
          border-l-2 border-brand
        ">
          <CornerUpLeft className="w-3.5 h-3.5 text-brand mt-0.5 shrink-0" strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-brand leading-tight">
              {pendingReply.senderType === 'user' ? 'Antworte auf eigene Nachricht' : 'Antworte auf Support'}
            </p>
            <div className="flex items-center gap-1 mt-0.5">
              {pendingReply.hasAttachments && pendingReply.contentPreview.length === 0 && (
                <ImageIcon className="w-3 h-3 opacity-60 shrink-0" strokeWidth={2} />
              )}
              <p className="text-[12px] opacity-70 leading-snug truncate">
                {pendingReply.contentPreview.length > 0
                  ? pendingReply.contentPreview
                  : pendingReply.hasAttachments
                    ? 'Bild'
                    : 'Nachricht'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClearReply}
            aria-label="Antwort-Verweis entfernen"
            className="
              shrink-0 w-6 h-6 rounded-full
              flex items-center justify-center
              opacity-50 hover:opacity-100
              hover:bg-black/5 dark:hover:bg-white/5
              transition-all duration-150
            "
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* Vorschau-Strip — Bild-Thumbnail + Caption-Input pro Bild */}
      {hasImages && (
        <div className="px-3 pt-3 flex flex-wrap gap-3">
          {attached.map((a, idx) => (
            <div
              key={a.previewUrl}
              className="flex flex-col gap-1.5 w-[120px]"
            >
              <div className="relative w-[120px] h-[88px] rounded-lg overflow-hidden glass-surface-soft ring-1 ring-white/10 dark:ring-white/10">
                <img src={a.previewUrl} alt={a.file.name} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  aria-label="Bild entfernen"
                  className="
                    absolute top-1 right-1
                    w-5 h-5 rounded-full
                    bg-black/70 text-white
                    flex items-center justify-center
                    hover:bg-black/90
                    transition-colors duration-150
                  "
                >
                  <X className="w-3 h-3" strokeWidth={2.5} />
                </button>
              </div>
              <input
                type="text"
                value={a.caption}
                onChange={(e) => setCaption(idx, e.target.value)}
                placeholder="Bildtitel (optional)"
                maxLength={CAPTION_MAX}
                aria-label={`Bildtitel für ${a.file.name}`}
                className="
                  w-full
                  bg-transparent
                  text-[11px] leading-tight
                  placeholder:opacity-40
                  outline-none
                  px-1.5 py-1
                  border-b border-white/10 dark:border-white/10
                  focus:border-brand/60
                  transition-colors duration-150
                "
              />
            </div>
          ))}
        </div>
      )}

      {/* Fehler-Hinweis */}
      {error && (
        <p className="px-3 pt-2 text-[11px] text-amber-400">{error}</p>
      )}

      <div className="pl-1 pr-3 py-3 flex items-end gap-2">
        {/* Anhang-Button — siehe Send-Button-Kommentar zu pointerdown-Handling. */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onPointerDown={(e) => {
            if (e.pointerType !== 'mouse') {
              e.preventDefault();
            }
          }}
          disabled={!canAttachMore || isSending}
          aria-label="Bild anhängen"
          title={canAttachMore ? 'Bild anhängen (max. 3)' : 'Maximal 3 Bilder erreicht'}
          className={`
            shrink-0
            w-9 h-9 rounded-full
            flex items-center justify-center
            transition-all duration-150
            touch-manipulation
            ${
              canAttachMore && !isSending
                ? 'opacity-60 hover:opacity-100 hover:bg-gray-200/70 dark:hover:bg-white/5'
                : 'opacity-30 cursor-not-allowed'
            }
            focus:outline-none focus:ring-2 focus:ring-brand/40
          `}
        >
          <Paperclip className="w-4 h-4" strokeWidth={2} />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            if (e.target.files) addImages(e.target.files);
            // Reset value damit dasselbe File nochmal ausgewählt werden kann
            e.target.value = '';
          }}
          className="hidden"
        />

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="Nachricht schreiben…"
          rows={1}
          maxLength={10_000}
          aria-label="Nachricht eingeben"
          className="
            flex-1
            resize-none
            bg-white/80 dark:bg-white/5
            text-[16px] sm:text-[13px] leading-[20px]
            placeholder:opacity-40
            outline-none
            px-3 py-2
            min-h-[36px]
            rounded-2xl
            ring-1 ring-inset ring-gray-300/70 dark:ring-white/10
            focus:ring-brand/50 dark:focus:ring-brand/40
            transition-colors duration-150
          "
        />

        <button
          type="button"
          onClick={handleSend}
          // onMouseDown/onTouchStart mit preventDefault verhindert auf
          // Tablet/Mobile, dass beim Antippen des Send-Buttons der Fokus von
          // der Textarea genommen wird. Hintergrund: Wenn der User die
          // Tastatur ausblendet (Tap auf Chat-Hintergrund), verliert das
          // Textarea seinen Fokus. Tippt er dann auf Send, registriert der
          // Browser parallel einen Fokus-Wechsel und einen Click — das löst
          // auf manchen iPadOS-Versionen aus, dass der Click verschluckt wird,
          // bevor er durchläuft. preventDefault auf pointerdown lässt den
          // Klick-Handler trotzdem laufen, ohne den Fokus weiter zu verschieben.
          onPointerDown={(e) => {
            // Nur für Touch- und Pen-Inputs preventDefault — bei Maus stört
            // es Doppelklick/Selektion nicht.
            if (e.pointerType !== 'mouse') {
              e.preventDefault();
            }
          }}
          disabled={!canSend}
          aria-label="Nachricht senden"
          title="Senden (Enter)"
          className={`
            shrink-0
            w-9 h-9 rounded-full
            flex items-center justify-center
            transition-all duration-150
            touch-manipulation
            ${
              canSend
                ? 'bg-brand text-gray-950 hover:bg-brand-hover cursor-pointer'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }
            focus:outline-none focus:ring-2 focus:ring-brand/40
          `}
        >
          <Send className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
